import { inject, Injectable } from '@angular/core';
import * as ct from 'countries-and-timezones';
import { BehaviorSubject, combineLatest, interval, map, startWith } from 'rxjs';
import { JwtService } from '../jwt/jwt-service';

@Injectable({
  providedIn: 'root'
})
export class TimezoneFlag {
  private jwtService = inject(JwtService);
  private lastMinute: number = 0;
  private localTimeSubject = new BehaviorSubject<any>(this.buildLocalTime());
  localTime$ = this.localTimeSubject.asObservable();

  private countryTimeSubject = new BehaviorSubject<any>(null);
  countryTime$ = this.countryTimeSubject.asObservable();

  constructor() {
    interval(1000).subscribe(() => {
      const now = new Date();
      this.jwtService.token$;
      if (this.lastMinute !== now.getMinutes()) {
        this.lastMinute = now.getMinutes();
        this.localTimeSubject.next(this.buildLocalTime());
      }
    });

    this.jwtService.token$.subscribe(() => {
      this.countryTimeSubject.next(this.buildCountryTime());
    });
  }

  private getCountriesByTimezone(timezone: string): { code: string; name: string }[] {
    const tz = ct.getTimezone(timezone);
    if (!tz || !tz.countries || tz.countries.length === 0) return [];

    return tz.countries
      .map((code) => ct.getCountry(code))
      .filter((c) => !!c)
      .map((c) => ({ code: c.id, name: c.name }));
  }

  private buildLocalTime() {
    const now = new Date();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offsetMinutes = -now.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(offsetMinutes);
    const hours = Math.floor(absMinutes / 60)
      .toString()
      .padStart(2, '0');
    const minutes = (absMinutes % 60).toString().padStart(2, '0');
    const formattedOffset = `UTC${sign}${hours}:${minutes}`;

    const countries = this.getCountriesByTimezone(timezone);
    const systemRegion = new Intl.Locale(navigator.language).region;
    const country = countries.find((c) => c.code === systemRegion) || countries[0] || null;

    return {
      localTime: this.format(now),
      offset: formattedOffset,
      timezone,
      countries,
      country
    };
  }

  private buildCountryTime() {
    this.countryTime$ = combineLatest([this.jwtService.timezoneUtc$, interval(1000).pipe(startWith(0))]).pipe(
      map(([tz]) => {
        if (!tz) return null;

        const clean = tz.replace('UTC', '').trim();
        const [hStr, mStr] = clean.split(':');
        const h = Number(hStr);
        const m = Number(mStr);

        const offsetMs = (h * 60 + (h >= 0 ? m : -m)) * 60 * 1000;
        const utcNow = new Date(Date.now() + new Date().getTimezoneOffset() * 60000);
        const countryDate = new Date(utcNow.getTime() + offsetMs);

        return {
          localTime: this.format(countryDate),
          offset: tz
        };
      })
    );
  }

  private format(date: Date): string {
    return new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    })
      .format(date)
      .replace(/\//g, '-');
  }
}
